import { useState, useEffect, useCallback } from 'react';
import { api, getToken, clearToken, UnauthorizedError } from './api/client';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Offers from './pages/Offers';
import Cessions from './pages/Cessions';
import Hosts from './pages/Hosts';
import Refunds from './pages/Refunds';
import SignalIntegrity from './pages/SignalIntegrity';
import Surveys from './pages/Surveys';
import Guide from './pages/Guide';

const PAGE_TITLES: Record<string, string> = {
  '/': 'Dashboard',
  '/offers': 'Anciennes offres en attente',
  '/cessions': 'Cessions en attente',
  '/hosts': 'Hôtes en attente',
  '/refunds': 'Remboursements',
  '/signal': 'Intégrité du signal',
  '/surveys': 'Sondage bêta',
  '/guide': 'Guide de l’exploitant',
};

export default function App() {
  const [pathname, setPathname] = useState(() =>
    PAGE_TITLES[window.location.pathname] ? window.location.pathname : '/',
  );
  const [authed, setAuthed] = useState(() => Boolean(getToken()));
  const [offerCount, setOfferCount] = useState(0);
  const [cessionCount, setCessionCount] = useState(0);
  const [hostCount, setHostCount] = useState(0);

  // A token sitting in sessionStorage may still be expired, so the first real
  // request is what actually proves the session. Any 401 drops back to login
  // rather than leaving an empty dashboard that looks like "no work pending".
  const handleAuthError = useCallback((err: unknown) => {
    if (err instanceof UnauthorizedError) setAuthed(false);
  }, []);

  useEffect(() => {
    if (!authed) return;
    api
      .getPendingOffers()
      .then((r) => setOfferCount(r.data.length))
      .catch(handleAuthError);
    api
      .getPendingCessions()
      .then((r) => setCessionCount(r.data.length))
      .catch(handleAuthError);
    api
      .getPendingHosts()
      .then((r) => setHostCount(r.data.length))
      .catch(handleAuthError);
  }, [authed, handleAuthError]);

  useEffect(() => {
    const handlePopState = () => {
      setPathname(
        PAGE_TITLES[window.location.pathname] ? window.location.pathname : '/',
      );
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = useCallback((nextPath: string) => {
    if (!PAGE_TITLES[nextPath] || nextPath === window.location.pathname) return;
    window.history.pushState({}, '', nextPath);
    setPathname(nextPath);
  }, []);

  const handleLogout = useCallback(() => {
    clearToken();
    setAuthed(false);
  }, []);

  if (!authed) {
    return <Login onSuccess={() => setAuthed(true)} />;
  }

  const title = PAGE_TITLES[pathname];
  const page =
    pathname === '/offers' ? (
      <Offers onCountChange={setOfferCount} />
    ) : pathname === '/cessions' ? (
      <Cessions onCountChange={setCessionCount} />
    ) : pathname === '/hosts' ? (
      <Hosts onCountChange={setHostCount} />
    ) : pathname === '/refunds' ? (
      <Refunds />
    ) : pathname === '/signal' ? (
      <SignalIntegrity />
    ) : pathname === '/surveys' ? (
      <Surveys />
    ) : pathname === '/guide' ? (
      <Guide />
    ) : (
      <Dashboard />
    );

  return (
    <Layout
      counts={{ offers: offerCount, cessions: cessionCount, hosts: hostCount }}
      title={title}
      pathname={pathname}
      onNavigate={navigate}
      onLogout={handleLogout}
    >
      {page}
    </Layout>
  );
}
